import { useCallback, useEffect } from 'react';
import { captureMessage } from '@sentry/react';

import * as service from '@services';
import {
  OrganizationCreditsState,
  setOrganizationCredits,
  setOrganizationCreditsLoading,
  setUserOrganizationCredits,
} from '@slices';
import {
  configSelector,
  contextNamesSelector,
  userOrganizationsSelector,
} from '@selectors';
import { useDispatch, useSelector } from '@hooks';
import { getOrganizationName } from '@utils';

export const useOrganizationCredits = () => {
  const dispatch = useDispatch();
  const { organizationName } = useSelector(contextNamesSelector);
  const { adminUrl } = useSelector(configSelector);
  const userOrganizations = useSelector(userOrganizationsSelector);

  const setLoading = useCallback(
    (loading: OrganizationCreditsState['loading']) => {
      dispatch(setOrganizationCreditsLoading(loading));
    },
    [dispatch],
  );

  const setCredits = useCallback(
    (
      type: 'user' | 'organization',
      credits?:
        | OrganizationCreditsState['organizationCredits']
        | OrganizationCreditsState['userOrganizationCredits'],
    ) => {
      const method =
        type === 'user' ? setUserOrganizationCredits : setOrganizationCredits;

      dispatch(method(credits));
    },
    [dispatch],
  );

  /**
   * Update user organization credits on every time
   * when organization is changed
   */
  useEffect(() => {
    if (!organizationName) {
      return;
    }

    const userOrganization = userOrganizations.find(
      ({ orgName }) => orgName === organizationName,
    );

    if (userOrganization) {
      setCredits('user', userOrganization.balance.credits);
    }
  }, [organizationName, userOrganizations, setCredits]);

  useEffect(() => {
    const run = async () => {
      if (!organizationName) {
        /**
         * Reset organization credits
         */
        setCredits('organization');

        return;
      }

      try {
        setLoading(true);

        const settings = await service.getOrganizationSettings({
          adminUrl,
          organizationName,
        });

        const organizationCredits = settings?.balance?.credits;

        setCredits('organization', organizationCredits);

        setLoading(false);
      } catch (error) {
        captureMessage(
          `Cannot parse organization balance for ${getOrganizationName(
            organizationName,
          )} organization`,
        );
      }
    };

    run();
  }, [adminUrl, organizationName, setLoading, setCredits]);
};
